import {
  useRef, useState, useEffect, useMemo,
} from 'react';
import { KLU_URL } from 'constants/klu';
import Layout from 'components/Layout';
import useKLU from 'hooks/useKLU';
import { validateSession } from 'modules/session/actions';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Loading from 'components/Loading';
import {
  checkCameraPermission,
  checkLocationPermission,
  getCameraPermission,
  getLocationPermission,
} from 'utils/events';
import platform from 'utils/platform';
import useLiterals from 'utils/hooks/useLiterals';
import { ReactComponent as LocationIcon } from 'assets/icons/geolocation.svg';
import { ReactComponent as CameraIcon } from 'assets/icons/camera.svg';
import Switch from 'components/UI/Switch';
import Button from 'components/UI/Button';
import InlineError from 'components/UI/InlineError';
import Translate from 'components/Translate';
import DocumentValidationModal from 'components/DocumentValidationModal';
import './styles.scss';

const STEPS = {
  DOCUMENT_VALIDATION: 'document_validation',
  PERMISSIONS: 'permissions',
  WEBVIEW: 'webview',
};

/** Origins allowed for KLU iframe postMessage (DOWNLOAD_FILE from wallet). */
const KLU_ORIGINS = ['https://corporate.klu.mx', 'https://corporate.qa-klu.mx'];

const KLU = () => {
  const literals = useLiterals('klu');
  const navigate = useNavigate();
  const iframeRef = useRef(null);
  const { user } = useSelector((state) => state.session);
  const klu = useKLU();
  const dispatch = useDispatch();

  const forceLogin = useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('login') === 'true';
  }, []);

  // Determinar si estamos en el flujo de signup (únete a KLU) o login (acceder)
  const isSignupFlow = useMemo(() => {
    return !klu.account && !forceLogin;
  }, [klu.account, forceLogin]);

  const [step, setStep] = useState(null);
  const [permissions, setPermissions] = useState({
    camera: false,
    location: false,
  });
  const [requestPermissions, setRequestPermissions] = useState({
    camera: false,
    location: false,
  });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  // Solo mostrar validación de documentos en el flujo de signup
  const [showDocumentValidation, setShowDocumentValidation] = useState(isSignupFlow);

  // Actualizar showDocumentValidation cuando cambie el flujo
  useEffect(() => {
    if (!isSignupFlow) {
      setShowDocumentValidation(false);
    }
  }, [isSignupFlow]);

  useEffect(() => {
    // Solo mostrar validación de documentos en el flujo de signup
    // Primero validar documentos, luego permisos
    if (showDocumentValidation && isSignupFlow) {
      setStep(STEPS.DOCUMENT_VALIDATION);
      setLoading(false);
      return;
    }

    const continuePermissions = (location, camera) => {
      if (!camera || !location) {
        setPermissions({ camera, location });
        setRequestPermissions({ camera: !camera, location: !location });
        setStep(STEPS.PERMISSIONS);
        setLoading(false);
      } else {
        setStep(STEPS.WEBVIEW);
      }
    };

    checkLocationPermission().then(async (locGranted) => {
      let camera = true;
      if (!klu.account) {
        camera = await checkCameraPermission();
      }

      let nextLocation = locGranted;
      if (!nextLocation) {
        const granted = await getLocationPermission();
        nextLocation = !!granted;
      }

      continuePermissions(nextLocation, camera);
    });
  }, [klu?.account, showDocumentValidation, isSignupFlow]);

  const handleBack = () => {
    if (!klu.account) {
      dispatch(validateSession(false, true));
    }
    navigate(-1);
  };

  const onFrameLoad = () => {
    setLoading(false);
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (loading) {
        setLoading(false);
      }
    }, 30000);

    return () => clearTimeout(timeoutId);
  }, [loading]);

  useEffect(() => {
    const handleKluMessage = (e) => {
      if (!KLU_ORIGINS.includes(e.origin)) return;
      if (!e.data || e.data.type !== 'DOWNLOAD_FILE') return;
      const { base64, mimeType, fileName } = e.data.payload || {};
      if (base64 && mimeType && fileName) {
        window.saveBlobFile(base64, mimeType, fileName);
      }
    };
    window.addEventListener('message', handleKluMessage);
    return () => window.removeEventListener('message', handleKluMessage);
  }, []);

  const handleLocationPermissions = () => {
    setError(null);
    if (permissions?.location) {
      setPermissions((prev) => ({ ...prev, location: false }));
    } else {
      getLocationPermission().then((permission) => {
        if (permission) {
          setPermissions((prev) => ({ ...prev, location: true }));
        } else {
          setError(literals.errorLocationPermissions);
        }
      });
    }
  };
  const handleCameraPermissions = () => {
    setError(null);
    if (permissions?.camera) {
      setPermissions((prev) => ({ ...prev, camera: false }));
    } else {
      getCameraPermission().then((permission) => {
        if (permission) {
          setPermissions((prev) => ({ ...prev, camera: true }));
        } else {
          // En navegador: mensaje específico explicando cómo habilitar el permiso.
          const msg = !platform.isNativeApp
            ? (literals.errorCameraPermissionsBrowser || literals.errorCameraPermissions)
            : literals.errorCameraPermissions;
          setError(msg);
        }
      });
    }
  };

  const handleContinue = () => {
    setLoading(true);
    setStep(STEPS.WEBVIEW);
  };

  const handleDocumentValidationContinue = () => {
    setShowDocumentValidation(false);
    setLoading(true);
    // El useEffect se encargará de continuar con los permisos
  };

  const handleDocumentValidationCancel = () => {
    navigate(-1);
  };

  // Configuración de documentos a validar (modular)
  const requiredDocuments = useMemo(() => [
    {
      id: 'ine',
      label: literals.documentValidation?.ine || 'Tengo mi INE (Identificación oficial) a la mano',
    },
    {
      id: 'rfc',
      label: literals.documentValidation?.rfc || 'Tengo mi RFC con homoclave disponible',
    },
    {
      id: 'proofOfAddress',
      label: literals.documentValidation?.proofOfAddress || 'Tengo mi comprobante de domicilio listo',
    },
    {
      id: 'googleAuthenticator',
      label: literals.documentValidation?.googleAuthenticator || 'Tengo Google Authenticator instalado en mi dispositivo',
    },
  ], [literals]);

  return (
    <Layout
      className='klu-layout'
      headerMenu
      bottomMenu={false}
      rightMenu={false}
      onBack={handleBack}
    >
      {loading && (
        <div className='loading'>
          <Loading force />
        </div>
      )}

      <DocumentValidationModal
        documents={requiredDocuments}
        title={literals.documentValidation?.title || 'Preparación para el proceso'}
        description={literals.documentValidation?.description || 'Para continuar con tu registro en KLU, asegúrate de contar con lo siguiente:'}
        continueText={literals.documentValidation?.continue || literals.common?.continue || 'Continuar'}
        cancelText={literals.documentValidation?.cancel || literals.common?.back || 'Cancelar'}
        isOpen={step === STEPS.DOCUMENT_VALIDATION}
        onContinue={handleDocumentValidationContinue}
        onCancel={handleDocumentValidationCancel}
      />

      {step === STEPS.WEBVIEW ? (
        <div className='iframe-container'>
          <iframe
            ref={iframeRef}
            className='embed-page with-menu'
            src={`${
              klu.account || forceLogin ? KLU_URL.login : KLU_URL.signup
            }?utm_source=Montepio&utm_id=${user?.id}`}
            title='KLU'
            allow='camera; microphone; geolocation; clipboard-read; clipboard-write; accelerometer; gyroscope; encrypted-media; midi; camera; microphone; geolocation; fullscreen; autoplay; storage-access'
            sandbox='allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation allow-modals allow-downloads'
            onLoad={onFrameLoad}
            onError={onFrameLoad}
          />
        </div>
      ) : null}

      {step === STEPS.PERMISSIONS && (
        <div className='full-height'>
          <div className='full-height-body'>
            <p className='font-bold text-primary mb-6 text-xl'>{literals.permissions}</p>
            <p className='mb-6'>
              {requestPermissions.camera && requestPermissions.location
                ? literals.beforeContinue
                : literals.beforeContinueSingle}
            </p>
            {requestPermissions.camera && (
              <Translate className='mb-6 mt-6' literal={literals.cameraDescription} />
            )}
            {requestPermissions.location && (
              <Translate className='mb-6 mt-6' literal={literals.locationDescription} />
            )}
          </div>
          <div className='full-height-footer'>
            {requestPermissions.camera && (
              <div className='klu-permissions-item flex items-center justify-between gap-4 mb-4'>
                <div className='flex items-center gap-1' onClick={handleCameraPermissions}>
                  <div className='switch-icon'>
                    <CameraIcon className='icon-primary' height={22} />
                  </div>
                  <p className='mb-0'>{literals.cameraPermissions}</p>
                </div>
                <Switch checked={permissions?.camera} onChange={handleCameraPermissions} />
              </div>
            )}
            {requestPermissions.location && (
              <div className='klu-permissions-item flex items-center justify-between gap-4 mb-6'>
                <div
                  className='flex items-center gap-1'
                  onClick={handleLocationPermissions}
                >
                  <div className='switch-icon'>
                    <LocationIcon className='icon-primary' height={26} />
                  </div>
                  <p className='mb-0'>{literals.locationPermissions}</p>
                </div>
                <Switch checked={permissions?.location} onChange={handleLocationPermissions} />
              </div>
            )}
            {error && <InlineError error={error} />}
            <Button
              className='mb-4'
              text={literals.common.continue}
              disabled={
                (!permissions?.location && requestPermissions.location)
                || (!permissions?.camera && requestPermissions.camera)
              }
              onClick={handleContinue}
            />
            <Button text={literals.common.back} type='secondary' onClick={() => navigate(-1)} />
          </div>
        </div>
      )}
    </Layout>
  );
};

export default KLU;
